import { type QueryKey } from '@tanstack/react-query';
import { create } from 'zustand';

export interface StaleMark {
  queryKey: QueryKey;
  /**
   * When the broadcast landed. A fetch that finished before this carries pre-event data, so
   * it cannot answer the mark - see `resolveStale`.
   */
  markedAt: number;
}

interface StaleViewStore {
  /**
   * Query keys a live broadcast has marked stale, deduped by serialized form. Global on
   * purpose, like the toast it replaced: the staleness belongs to the caches, not to the
   * view that happened to be on screen when the broadcast landed.
   */
  staleMarks: Array<StaleMark>;
  markStale: (queryKeys: Array<QueryKey>) => void;
  /**
   * A query has fetched, so anything it covers is current again. Prefix match, the way
   * react-query's own partial matching works: a mark on `[AssetData]` is answered by
   * `[AssetData, folderId, page]` arriving. This is what takes the indicator down when the
   * viewer navigates somewhere that refetches, instead of leaving it pulsing until clicked.
   *
   * `fetchedAt` guards the race where a query already in flight when the broadcast landed
   * resolves the mark with data that predates the event.
   */
  resolveStale: (fetchedQueryKey: QueryKey, fetchedAt: number) => void;
  clearStale: () => void;
}

const serialize = (queryKey: QueryKey): string => JSON.stringify(queryKey);

// A mark longer than the fetched key falls out of `every` on its own: the extra segment has
// no counterpart, and `[segment]` never serializes equal to `[undefined]`.
export const covers = (mark: QueryKey, fetched: QueryKey): boolean =>
  mark.every((segment, index) => serialize([segment]) === serialize([fetched[index]]));

export const useStaleViewStore = create<StaleViewStore>((set) => ({
  staleMarks: [],
  // Returns the same state when nothing is new, so a broadcast burst re-marking the same
  // keys does not re-render every subscriber per frame.
  markStale: (queryKeys: Array<QueryKey>) =>
    set((state) => {
      const seen = new Set(state.staleMarks.map((mark) => serialize(mark.queryKey)));
      const fresh = queryKeys.filter((queryKey) => !seen.has(serialize(queryKey)));
      if (fresh.length === 0) {
        return state;
      }
      const markedAt = Date.now();
      return { ...state, staleMarks: [...state.staleMarks, ...fresh.map((queryKey) => ({ queryKey, markedAt }))] };
    }),
  resolveStale: (fetchedQueryKey: QueryKey, fetchedAt: number) =>
    set((state) => {
      if (state.staleMarks.length === 0) {
        return state;
      }
      const remaining = state.staleMarks.filter(
        (mark) => !(covers(mark.queryKey, fetchedQueryKey) && fetchedAt >= mark.markedAt),
      );
      return remaining.length === state.staleMarks.length ? state : { ...state, staleMarks: remaining };
    }),
  clearStale: () => set((state) => (state.staleMarks.length === 0 ? state : { ...state, staleMarks: [] })),
}));
